import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API_URL as BACKEND_URL } from '../api/apiUrl';

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => {
  if (isDev) console.log(...args);
};

const WELCOME = '¡Hola! Soy el asistente virtual de Notaría Pública 90. ¿En qué puedo ayudarte hoy?\n\nPuedo ayudarte con:\n• Agendar citas\n• Contactar con abogados especializados\n• Generar cotizaciones\n• Informar sobre requisitos de trámites';

const ChatBot = ({ onClose }) => {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: WELCOME
  }]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/message`, {
        message: inputMessage,
        session_id: sessionId
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        action_detected: response.data.action_detected,
        action_data: response.data.action_data
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      // Handle actions
      if (response.data.action_detected) {
        handleAction(response.data.action_detected, response.data.action_data);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta de nuevo.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = (action, data) => {
    switch (action) {
      case 'schedule_appointment':
        devLog('Schedule appointment action');
        break;
      case 'contact_lawyer':
        devLog('Contact lawyer action:', data);
        break;
      case 'request_quote':
        devLog('Request quote action:', data);
        break;
      case 'get_requirements':
        devLog('Get requirements action:', data);
        break;
      default:
        break;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Window */}
        <div data-testid="chatbot-window" className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border-2 border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-notaria-primary to-notaria-secondary text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold">Asistente Virtual</h3>
                <p className="text-xs text-gray-100">Notaría Pública 90</p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="chatbot-close-btn"
              className="hover:bg-white/20 p-2 rounded-lg transition-colors"
              aria-label="Cerrar chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-notaria-secondary text-white'
                      : 'bg-white text-gray-800 shadow-md border border-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap text-left">{message.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 shadow-md border border-gray-100">
                  <Loader2 className="w-5 h-5 animate-spin text-notaria-secondary" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <Input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                data-testid="chatbot-input"
                className="flex-1"
                disabled={isLoading}
              />
              <Button
                onClick={handleSendMessage}
                data-testid="chatbot-send-btn"
                disabled={isLoading || !inputMessage.trim()}
                className="bg-notaria-secondary hover:bg-notaria-secondary text-white"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Powered by Claude AI
            </p>
          </div>
        </div>
    </>
  );
};

export default ChatBot;
